import { useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { SiteProvider } from "@/context/SiteContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import QuoteFlowV2 from "@/components/QuoteFlowV2";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import HowItWorks from "@/pages/HowItWorks";
import Projects from "@/pages/Projects";
import About from "@/pages/About";
import Partners from "@/pages/Partners";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Reclamatii from "@/pages/Reclamatii";
import NotFound from "@/pages/NotFound";
import CookieConsent from "@/components/CookieConsent";
import { initGa4, applyGa4Consent, getConsent } from "@/lib/consent";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function AnalyticsConsent() {
  useEffect(() => {
    initGa4();
    applyGa4Consent(getConsent());
    const onChange = (e) => applyGa4Consent(e.detail);
    window.addEventListener("brox-consent-change", onChange);
    return () => window.removeEventListener("brox-consent-change", onChange);
  }, []);
  return null;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink-950 px-6 text-center">
          <p className="font-display text-3xl font-bold uppercase text-paper">Ceva n-a funcționat</p>
          <p className="text-muted-foreground">Reîmprospătează pagina sau încearcă din nou în câteva momente.</p>
          <a href="/" className="btn-primary" data-testid="error-reload-btn">ÎNAPOI LA ÎNCEPUT</a>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.__lenis = lenis;
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function SiteChrome() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  if (isAdmin) return null;
  return (
    <>
      <Navbar />
      <FloatingWhatsApp />
      <CookieConsent />
    </>
  );
}

function SiteFooter() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return <Footer />;
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <SiteProvider>
          <AnalyticsConsent />
          <BrowserRouter>
            <ScrollManager />
            <SiteChrome />
            <QuoteFlowV2 />
            <main id="main">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/servicii" element={<Services />} />
                <Route path="/servicii/:slug" element={<ServiceDetail />} />
                <Route path="/cum-functioneaza" element={<HowItWorks />} />
                <Route path="/proiecte" element={<Projects />} />
                <Route path="/despre-noi" element={<About />} />
                <Route path="/parteneri" element={<Partners />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/reclamatii" element={<Reclamatii />} />
                <Route path="/termeni-conditii" element={<Legal page="terms" />} />
                <Route path="/politica-confidentialitate" element={<Legal page="privacy" />} />
                <Route path="/politica-cookies" element={<Legal page="cookies" />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <SiteFooter />
          </BrowserRouter>
        </SiteProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
